import { api, ApiTags } from '../../api';
import { PromptDto } from '../../../components/types';
import { promptsActions } from '../../slices/prompts';

import { PromptRequest, PromptResponse, MyPromptsResponse, UserPromptsRequest } from './types';
import { transformPromptDtoToModel } from './mappers';

const promptApi = api.injectEndpoints({
    endpoints: build => ({
        // Получить промпт по id
        getPrompt: build.query<PromptResponse, PromptRequest>({
            query: ({ promptId }) => `/prompts/${promptId}`,
            transformResponse: transformPromptDtoToModel,
            providesTags: (result, _, request) => (result ? [{ type: ApiTags.Prompts, id: request.promptId }] : []),
            onQueryStarted: async ({}, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    // Добавляем промпт в EntityAdapter кеш
                    dispatch(promptsActions.addPrompts([data]));
                } catch {
                    // Ошибка обрабатывается RTK Query
                }
            },
        }),

        // Получить похожие промпты по id промпта
        getSimilarPrompts: build.query<PromptDto[], PromptRequest>({
            query: ({ promptId }) => ({ url: '/prompts', params: { hash: promptId } }),
            transformResponse: (response: { prompts: PromptDto[] }) => response.prompts.map(transformPromptDtoToModel),
            serializeQueryArgs: ({ endpointName }) => endpointName,
            providesTags: result => {
                if (!result) {
                    return [{ type: ApiTags.Prompts, id: 'LIST' }];
                }

                return [
                    ...result.map(({ id }) => ({ type: ApiTags.Prompts, id })),
                    // { type: ApiTags.Prompts, id: 'LIST' },
                ];
            },
            onQueryStarted: async ({ promptId }, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    // Сохраняем PromptDto напрямую в EntityAdapter
                    dispatch(promptsActions.addPrompts(data));
                    dispatch(promptsActions.setSimilarPrompts({ promptId, prompts: data }));
                } catch {
                    // Ошибка обрабатывается RTK Query
                }
            },
        }),

        // Получить 3 популярных промпта
        getPopularPrompts: build.query<PromptDto[], void>({
            query: () => '/prompts/popular',
            transformResponse: (response: PromptDto[]) => response.map(transformPromptDtoToModel),
            providesTags: [{ type: ApiTags.Prompts, id: 'POPULAR' }],
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    // Сохраняем PromptDto напрямую в EntityAdapter
                    dispatch(promptsActions.addPrompts(data));
                    dispatch(promptsActions.setPopularPrompts(data));
                } catch {
                    // Ошибка обрабатывается RTK Query
                }
            },
        }),

        // Получить все промпты текущего пользователя
        getMyPrompts: build.query<PromptDto[], void>({
            query: () => '/prompts/user/my',
            transformResponse: (response: MyPromptsResponse) => response.map(transformPromptDtoToModel),
            providesTags: [{ type: ApiTags.Prompts, id: 'MY' }],
            keepUnusedDataFor: 0,
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    // Сохраняем PromptDto напрямую в EntityAdapter
                    dispatch(promptsActions.addPrompts(data));
                    dispatch(promptsActions.setMyPrompts(data));
                } catch {
                    // Ошибка обрабатывается RTK Query
                }
            },
        }),
        // Получить промпты пользователя по login или publicId
        getUserPrompts: build.query<PromptDto[], UserPromptsRequest>({
            query: ({ loginOrPublicId }) => `/prompts/user/${loginOrPublicId}`,
            transformResponse: (response: MyPromptsResponse) => response,
            providesTags: result => {
                if (!result) {
                    return [{ type: ApiTags.Prompts, id: 'USER_PROMPTS' }];
                }

                return [
                    ...result.map(({ id }) => ({ type: ApiTags.Prompts, id })),
                    { type: ApiTags.Prompts, id: 'USER_PROMPTS' },
                ];
            },
        }),
    }),
});

export const {
    useGetPromptQuery,
    useGetSimilarPromptsQuery,
    useGetUserPromptsQuery,
    useGetMyPromptsQuery,
    useGetPopularPromptsQuery,
} = promptApi;

export { promptApi };
